import { DateTime } from "luxon";

import { PagedResult } from "../DTOs/Common/PagedResult";
import { AddGeoProductionMiningMovementDTO } from "../DTOs/Geology/AddGeoProductionMiningMovementDTO";
import { GeoCrushingLoaderDTO } from "../DTOs/Geology/GeoCrushingLoaderDTO";
import GeoMillDTO from "../DTOs/Geology/GeoMillDTO";
import { GeoMiningMovementDTO } from "../DTOs/Geology/GeoMiningMovementDTO";
import { GeoMiningProductionExcavatorDTO } from "../DTOs/Geology/GeoMiningProductionExcavatorDTO";
import { GeoMiningProductionTruckDTO } from "../DTOs/Geology/GeoMiningProductionTruckDTO";
import { GeoOreBlockDTO } from "../DTOs/Geology/GeoOreBlockDTO";
import { GeoOreBlockEntryDTO } from "../DTOs/Geology/GeoOreBlockEntryDTO";
import { GeoOreBlockEntryDetailDTO } from "../DTOs/Geology/GeoOreBlockEntryDetailDTO";
import { GeoOreTallyMiningProductionListEntryDTO } from "../DTOs/Geology/GeoOreTallyMiningProductionListEntryDTO";
import { GeoOreTruckDTO } from "../DTOs/Geology/GeoOreTruckDTO";
import { GeoProductionEntryDTO } from "../DTOs/Geology/GeoProductionEntryDTO";
import { GeoRehandleDTO } from "../DTOs/Geology/GeoRehandleDTO";
import { GeoRehandleDiggerDTO } from "../DTOs/Geology/GeoRehandleDiggerDTO";
import { GeoRehandleTruckingTruckDTO } from "../DTOs/Geology/GeoRehandleTruckingTruckDTO";
import { UpdateOreTallyMiningProductionEntryRequest } from "../DTOs/Geology/UpdateOreTallyMiningProductionEntryRequestDTO";
import { DataTableFilteredRequest } from "../components/DataTable/TableHook";
import { ajaxController, formatDatesForDateOnlyModel } from "./controller/AjaxController";
import GeoDrillingEntryDTO from "../DTOs/Geology/GeoDrillingEntryDTO";
import { DoubleShift, TripleShift } from "../types/Shift";
import { LocationDTO } from "../DTOs/Production/LocationDTO";
import { GetGeoOreBlocksFilteredRequest } from "../DTOs/Geology/GetGeoOreBlocksFilteredRequest";
import { GeoOreTallyPagedResult } from "../DTOs/Geology/GeoOreTallyPagedResult";
import { GetGeoRehandlesPagedResultDTO } from "../DTOs/Geology/GetGeoRehandlesPagedResultDTO";
import { GeoRehandleMutationResponseDTO } from "../DTOs/Geology/GeoRehandleMutationResponseDTO";
import { GetGeoMiningMvtTruckSummaryPagedResultDTO } from "../DTOs/Geology/GetGeoMiningMvtTruckSummaryPagedResultDTO";
import { GetOreTallyMiningProductionResultDto } from "../DTOs/Geology/GetOreTallyMiningProductionResultDTO";
import { GetOreTallyGeologyProductionResultDTO } from "../DTOs/Geology/GetOreTallyGeologyProductionResultDTO";

class GeologyAjax {
  private _ajaxController = ajaxController;
  private _geologyDataEndpoint = "/Geology";

  async getGeoOreTrucks(filters: DataTableFilteredRequest) {
    return await this._ajaxController.get<PagedResult<GeoOreTruckDTO>>(`${this._geologyDataEndpoint}/geology-ore-trucks`, filters);
  }

  async deleteGeoOreTruck(geoOreTruckId: number) {
    return await this._ajaxController.delete(`${this._geologyDataEndpoint}/geology-ore-trucks/${geoOreTruckId}`);
  }

  async getGeoMiningMovementById(geoMiningMovementId: number) {
    return await this._ajaxController.get<GeoMiningMovementDTO>(`${this._geologyDataEndpoint}/geology-mining-movements/${geoMiningMovementId}`, {});
  }

  async getGeoMiningMovement(shiftDate: DateTime, dayOrNight: DoubleShift, diggerId: number, pitStageId: number) {
    return await this._ajaxController.get<GeoMiningMovementDTO | undefined>(
      `${this._geologyDataEndpoint}/geology-mining-movements/${shiftDate.toISODate()}/${dayOrNight[0]}/${diggerId}/${pitStageId}`,
      {});
  }

  async getGeoMiningMovements(filters: DataTableFilteredRequest) {
    return await this._ajaxController.get<PagedResult<GeoMiningMovementDTO>>(
      `${this._geologyDataEndpoint}/geology-mining-movements`,
      filters
    );
  }

  async addGeoMiningMovement(data: GeoMiningMovementDTO) {
    return await this._ajaxController.post(`${this._geologyDataEndpoint}/geology-mining-movements`, data);
  }

  async addGeoProductionMiningMovement(data: AddGeoProductionMiningMovementDTO) {
    return await this._ajaxController.post<AddGeoProductionMiningMovementDTO, GeoMiningMovementDTO>(
      `${this._geologyDataEndpoint}/geology-mining-movements/production`,
      data,
      formatDatesForDateOnlyModel
    );
  }

  async updateGeoMiningMovement(data: GeoMiningMovementDTO) {
    return await this._ajaxController.put<GeoMiningMovementDTO>(
      `${this._geologyDataEndpoint}/geology-mining-movements/${data.geoMiningMvtId}`,
      data,
      formatDatesForDateOnlyModel
    );
  }

  async deleteFilterFromMovementEntry(geoMiningMovementId: number, oreBlockToDelete: string) {
    return await this._ajaxController.delete(`${this._geologyDataEndpoint}/geology-mining-movements/${geoMiningMovementId}/${oreBlockToDelete}`);
  }

  async deleteGeoMiningMovement(geoMiningMovementId: number) {
    return await this._ajaxController.delete(`${this._geologyDataEndpoint}/geology-mining-movements/${geoMiningMovementId}`);
  }

  async getGeoProductionEntries(geoMiningMovementId: number) {
    return await this._ajaxController.get<PagedResult<GeoProductionEntryDTO>>(`${this._geologyDataEndpoint}/geology-production-entries/${geoMiningMovementId}`, {});
  }

  async updateGeoProductionEntry(data: GeoProductionEntryDTO) {
    return await this._ajaxController.put(`${this._geologyDataEndpoint}/geology-production-entries/${data.geoOreTruckId}`, data);
  }

  async addGeoProductionEntry(data: GeoProductionEntryDTO) {
    return await this._ajaxController.post<GeoProductionEntryDTO, GeoProductionEntryDTO>(`${this._geologyDataEndpoint}/geology-production-entries`, data);
  }

  async getGeoOreBlock(geoOreBlockId: number) {
    return await this._ajaxController.get<GeoOreBlockEntryDTO>(`${this._geologyDataEndpoint}/geology-ore-blocks/${geoOreBlockId}`, {});
  }

  async getGeoOreBlocks(filters: GetGeoOreBlocksFilteredRequest) {
    return await this._ajaxController.get<PagedResult<GeoOreBlockDTO>>(`${this._geologyDataEndpoint}/geology-ore-blocks`, filters);
  }

  async getGeoOreBlockEntry(geoOreBlockEntryId: number) {
    return await this._ajaxController.get<GeoOreBlockEntryDTO>(`${this._geologyDataEndpoint}/geology-ore-block-entries/${geoOreBlockEntryId}`, {});
  }

  async getGeoOreBlockEntries(filters: GetGeoOreBlocksFilteredRequest) {
    return await this._ajaxController.get<PagedResult<GeoOreBlockEntryDTO>>(`${this._geologyDataEndpoint}/geology-ore-block-entries`, filters);
  }

  async geoGeoProductionEntriesForOreBlock(geoOreBlockId: number) {
    return await this._ajaxController.get<PagedResult<GeoOreBlockEntryDetailDTO>>(`${this._geologyDataEndpoint}/geology-production-entries-with-ore-block/${geoOreBlockId}`, {});
  }

  async getUncompletedGeoOreBlockEntries(filters: GetGeoOreBlocksFilteredRequest) {
    return await this._ajaxController.get<PagedResult<GeoOreBlockEntryDTO>>(`${this._geologyDataEndpoint}/geology-ore-block-entries/uncompleted`, filters);
  }

  async addGeoOreBlock(data: GeoOreBlockEntryDTO) {
    return await this._ajaxController.post(`${this._geologyDataEndpoint}/geology-ore-blocks`, data);
  }

  async updateGeoOreBlock(data: GeoOreBlockEntryDTO) {
    return await this._ajaxController.put(`${this._geologyDataEndpoint}/geology-ore-blocks/${data.id}`, data);
  }

  async deleteGeoOreBlock(geoOreBlockId: number) {
    return await this._ajaxController.delete(`${this._geologyDataEndpoint}/geology-ore-blocks/${geoOreBlockId}`);
  }

  async getGeoRehandles(date: DateTime, dayNightAfternoon: TripleShift) {
    return await this._ajaxController.get<GetGeoRehandlesPagedResultDTO>(`${this._geologyDataEndpoint}/geology-rehandles/${date.toISODate()}/${dayNightAfternoon[0]}`, {});
  }

  async getGeoCrushingEntries(date: DateTime, dayNightAfternoon: TripleShift) {
    return await this._ajaxController.get<GetGeoRehandlesPagedResultDTO>(`${this._geologyDataEndpoint}/geology-crushing/${date.toISODate()}/${dayNightAfternoon[0]}`, {});
  }

  async addGeoRehandle(data: GeoRehandleDTO, date: DateTime, dayNightAfternoon: TripleShift) {
    return await this._ajaxController.post<GeoRehandleDTO, GeoRehandleMutationResponseDTO>(`${this._geologyDataEndpoint}/geology-rehandles/${date.toISODate()}/${dayNightAfternoon[0]}`, data);
  }

  async updateGeoRehandle(data: GeoRehandleDTO) {
    return await this._ajaxController.put<GeoRehandleDTO, GeoRehandleMutationResponseDTO>(
      `${this._geologyDataEndpoint}/geology-rehandles/${data.geoRehandleId}`,
      data,
      formatDatesForDateOnlyModel
    );
  }

  async deleteGeoRehandle(geoRehandleId: number) {
    return await this._ajaxController.delete<GeoRehandleMutationResponseDTO>(`${this._geologyDataEndpoint}/geology-rehandles/${geoRehandleId}`);
  }

  async getGeoOreTallyListEntries(dateFrom: DateTime, filters: DataTableFilteredRequest) {
    return await this._ajaxController.get<GeoOreTallyPagedResult>(`${this._geologyDataEndpoint}/geology-ore-tallys/${dateFrom.toISODate()}`,
      filters,
      undefined,
      formatDatesForDateOnlyModel
    );
  }

  async getGeoOreTallyMiningProductionEntries(dateFrom: DateTime, dayOrNight: DoubleShift, filters: DataTableFilteredRequest) {
    return await this._ajaxController.get<GetOreTallyMiningProductionResultDto>(`${this._geologyDataEndpoint}/geology-ore-tallys/mining-production/${dateFrom.toISODate()}/${dayOrNight[0]}`, filters);
  }

  async updateGeoOreMiningProductionEntry(data: UpdateOreTallyMiningProductionEntryRequest) {
    return await this._ajaxController.post<UpdateOreTallyMiningProductionEntryRequest, GeoOreTallyMiningProductionListEntryDTO>(`${this._geologyDataEndpoint}/geology-ore-tallys/mining-production`, data);
  }

  async getGeoOreTallyGeologyProductionEntries(dateFrom: DateTime, dayOrNight: DoubleShift, filters: DataTableFilteredRequest) {
    return await this._ajaxController.get<GetOreTallyGeologyProductionResultDTO>(`${this._geologyDataEndpoint}/geology-ore-tallys/geology-production/${dateFrom.toISODate()}/${dayOrNight[0]}`, filters);
  }

  async getGeoMiningProductionExcavators(shiftId: number, pitId: number) {
    return await this._ajaxController.get<PagedResult<GeoMiningProductionExcavatorDTO>>(
      `${this._geologyDataEndpoint}/mining-production-excavators/${shiftId}/${pitId}`,
      {}
    );
  }

  async getProductionTruckingTrucks(date: DateTime) {
    return await this._ajaxController.get<PagedResult<GeoMiningProductionTruckDTO>>(
      `${this._geologyDataEndpoint}/production-trucking-trucks/${date.toISODate()}`,
      {}
    );
  }

  async getRehandleDiggers(date: DateTime, dayNightAfternoon: TripleShift) {
    return await this._ajaxController.get<PagedResult<GeoRehandleDiggerDTO>>(
      `${this._geologyDataEndpoint}/rehandle-diggers/${date.toISODate()}/${dayNightAfternoon[0]}`,
      {}
    );
  }

  async getRehandleTruckingTrucks(date: DateTime) {
    return await this._ajaxController.get<PagedResult<GeoRehandleTruckingTruckDTO>>(
      `${this._geologyDataEndpoint}/trucking-trucks/${date.toISODate()}`,
      {}
    );
  }

  async getCrushingLoaders(date: DateTime, dayNightAfternoon: TripleShift) {
    return await this._ajaxController.get<PagedResult<GeoCrushingLoaderDTO>>(
      `${this._geologyDataEndpoint}/loaders/${date.toISODate()}/${dayNightAfternoon[0]}`,
      {}
    );
  }

  async getRehandleMovementFromLocations(date: DateTime) {
    return await this._ajaxController.get<PagedResult<LocationDTO>>(
      `${this._geologyDataEndpoint}/rehandle/locations/movement-from/${date.toISODate()}`,
      {}
    );
  }

  async getRehandleMovementToLocations(date: DateTime) {
    return await this._ajaxController.get<PagedResult<LocationDTO>>(
      `${this._geologyDataEndpoint}/rehandle/locations/movement-to/${date.toISODate()}`,
      {}
    );
  }

  async getCrushingMovementFromLocations(date: DateTime) {
    return await this._ajaxController.get<PagedResult<LocationDTO>>(
      `${this._geologyDataEndpoint}/crushing/locations/movement-from/${date.toISODate()}`,
      {}
    );
  }

  async getCrushingMovementToLocations(date: DateTime) {
    return await this._ajaxController.get<PagedResult<LocationDTO>>(
      `${this._geologyDataEndpoint}/crushing/locations/movement-to/${date.toISODate()}`,
      {}
    );
  }

  async getGeologyMillingEntries(filters: DataTableFilteredRequest) {
    return await this._ajaxController.get<PagedResult<GeoMillDTO>>(`${this._geologyDataEndpoint}/milling`,
      filters
    );
  }

  async getGeologyMillingDistinctMillIds() {
    return await this._ajaxController.get<PagedResult<number>>(`${this._geologyDataEndpoint}/milling/mills/ids`, {});
  }

  async addGeologyMillingEntry(data: GeoMillDTO) {
    return await this._ajaxController.post<GeoMillDTO, GeoMillDTO>(`${this._geologyDataEndpoint}/milling`,
      data,
      formatDatesForDateOnlyModel
    );
  }

  async updateGeologyMillingEntry(data: GeoMillDTO) {
    return await this._ajaxController.put<GeoMillDTO>(`${this._geologyDataEndpoint}/milling/${data.id}`,
      data,
      formatDatesForDateOnlyModel
    );
  }

  async deleteGeologyMillingEntry(geologyMillingEntryId: number) {
    return await this._ajaxController.delete(`${this._geologyDataEndpoint}/milling/${geologyMillingEntryId}`);
  }

  async getGeoMiningMovementsSummary(
    shiftDate: DateTime,
    dayOrNight: DoubleShift,
    filters: DataTableFilteredRequest
  ) {
    return await this._ajaxController.get<GetGeoMiningMvtTruckSummaryPagedResultDTO>(
      `${this._geologyDataEndpoint}/geology-mining-movements-summary/${shiftDate.toISODate()}/${dayOrNight[0]}`,
      filters
    );
  }

  async getGeoDrillingEntries(filters: DataTableFilteredRequest) {
    return await this._ajaxController.get<PagedResult<GeoDrillingEntryDTO>>(`${this._geologyDataEndpoint}/geology-drillings/entries`,
      filters,
      undefined,
      formatDatesForDateOnlyModel
    );
  }

  async saveGeoDrillingEntry(data: GeoDrillingEntryDTO) {
    return await this._ajaxController.post<GeoDrillingEntryDTO, GeoDrillingEntryDTO>(`${this._geologyDataEndpoint}/geology-drillings/entries`,
      data,
      formatDatesForDateOnlyModel
    );
  }

  async deleteGeoDrillingEntry(date: DateTime) {
    return await this._ajaxController.delete(`${this._geologyDataEndpoint}/geology-drillings/entries/${date.toISODate()}`);
  }
}

export const geologyAjax = new GeologyAjax();
